// Client-side barcode-PRESENCE detection for uploaded artwork. Sibling of
// detectArtwork.ts (size/color/sides/page count) and docThumbnail.ts
// (thumbnail) — same lazy,
// best-effort, never-throws discipline; this one answers a single question:
// "is there a barcode anywhere in this artwork?"
//
// We detect PRESENCE ONLY — we do not validate or decode the barcode. zxing
// necessarily reads a value to confirm a symbol is real, but that value is
// discarded here; we surface nothing but found / missing / unknown. The result
// drives a NON-BLOCKING customer advisory ("we didn't spot a barcode — adding
// one may cost extra") and is forwarded to the back office; it never gates an
// upload or a submission (V1).
//
//   PDF    — rasterize the first page(s) via the shared pdf.js loader, then
//            scan the pixels. (zxing can't read a PDF directly.)
//   PNG/JPEG — handed straight to zxing, which decodes the image itself; on a
//            miss we rasterize it ourselves to retry the inverted pass (below).
//   Other  — SVG / TIFF / ZIP / AI / EPS aren't reliably rasterizable here, so
//            we return 'unknown' (no signal → no warning) rather than guess.
//
// Each rasterized surface is scanned in BOTH polarities — as-is, then with the
// pixels inverted — because a barcode printed LIGHT-ON-DARK (e.g. a white
// ISBN/UPC on a navy book cover) is NOT read by zxing's own `tryInvert`, which
// flips AFTER binarization and fails over a textured dark ground. Inverting the
// RGB BEFORE zxing binarizes hands it a clean dark-on-light image it reads
// reliably (verified against a real white-on-navy cover that zxing missed every
// other way). The inverted pass still needs a valid checksum to "find" a symbol,
// so it does not invent barcodes on barcode-less artwork (verified against a
// barcode-less book interior — both passes correctly found nothing).
//
// zxing-wasm is imported DYNAMICALLY (only once a scannable file is in hand) so
// its ~1 MB wasm never lands in the main bundle — flat-sheet/USB customers who
// never upload scannable artwork never download it. The wasm itself is served
// SAME-ORIGIN (Vite ?url asset), NOT zxing's default jsDelivr CDN — the portal
// must stay self-hosted + cloud-portable with no external host dependency
// (CLAUDE.md §"Cloud-portable, no host lock-in").
//
// Returns 'unknown' for anything we can't scan confidently (unsupported type,
// oversized file, render/decoder failure) — we never report 'missing' unless we
// actually looked and found nothing, so the warning never cries wolf.

import type { BarcodeScan } from 'shared';
import type { ReaderOptions } from 'zxing-wasm/reader';

import { loadPdfjs } from './loadPdfjs.js';

// Skip scanning past this source size — rasterizing/decoding a very large file
// client-side risks an OOM stall for little gain. Mirrors the thumbnail +
// preview-proxy caps so behavior is consistent across the upload surfaces.
const MAX_SCAN_SOURCE_BYTES = 25 * 1024 * 1024;

// Longest-side render target for a PDF page, in px. Barcodes need far more
// resolution than the 320px thumbnail, and the demanding case is a BOOK COVER
// WRAP — back + spine + front in one wide page — where the ISBN/UPC barcode is a
// small region of a ~13-18" sheet. At the old 1600px cap that worked out to
// well under 2 px per barcode module on a wide wrap, so the bars blurred
// together and zxing read nothing (a real barcode reported as "missing"). 3500px
// puts a typical EAN/UPC symbol on a ~13" wrap at ~250+ DPI (3-4 px/module) —
// comfortably decodable — while a single transient canvas (~31 MB for a wide
// wrap, discarded immediately) stays well within budget. A page so large it
// can't render still fails safe: the catch below returns 'unknown', never a
// false "missing". The 4× scale cap keeps a small page (business card) from
// being blown up absurdly while still landing it near 288 DPI.
const PDF_SCAN_MAX_PX = 3500;

// How many leading pages of a PDF to scan. A mailing/return barcode is usually
// on the first sheet, but a 2-sided piece (postcard) often carries it on the
// back — so we check the first two pages before concluding "missing". We do NOT
// scan an entire book interior: it's slow and a barcode there is not what this
// advisory is about.
const MAX_PDF_PAGES_TO_SCAN = 2;

function isPdf(content_type: string, filename: string): boolean {
  return content_type === 'application/pdf' || filename.toLowerCase().endsWith('.pdf');
}

// Raster types zxing's bundled image decoder handles directly from the file
// bytes. PNG + JPEG cover the overwhelming majority of uploaded reference art;
// TIFF/SVG aren't decoded here (→ 'unknown').
function isDirectlyScannableImage(content_type: string, filename: string): boolean {
  const name = filename.toLowerCase();
  return (
    content_type === 'image/png' ||
    content_type === 'image/jpeg' ||
    name.endsWith('.png') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg')
  );
}

// zxing-wasm reader, configured once. We register a SAME-ORIGIN locateFile so
// the wasm is fetched from our own bundle (Vite ?url) instead of zxing's
// default jsDelivr CDN. Memoized so we configure + import a single time.
let readerPromise: Promise<typeof import('zxing-wasm/reader')> | null = null;
function loadReader(): Promise<typeof import('zxing-wasm/reader')> {
  if (!readerPromise) {
    readerPromise = (async () => {
      const reader = await import('zxing-wasm/reader');
      const wasmUrl = (await import('zxing-wasm/reader/zxing_reader.wasm?url')).default;
      reader.prepareZXingModule({
        overrides: {
          // Resolve EVERY asset same-origin: the wasm to our bundled ?url, and
          // any other file zxing might request to the SAME directory as that
          // url. We deliberately ignore the Emscripten-supplied `prefix` — it
          // defaults to zxing's jsDelivr CDN, and reaching it would reintroduce
          // the external-host dependency the cloud-portability rule forbids.
          locateFile: (path: string) =>
            path.endsWith('.wasm') ? wasmUrl : wasmUrl.replace(/[^/]*$/, path),
        },
      });
      return reader;
    })();
  }
  return readerPromise;
}

// zxing reader options for a presence scan. These mirror zxing's own defaults
// (tryHarder / tryRotate / tryInvert / tryDownscale all on) but are set
// EXPLICITLY so a future zxing default change can't silently weaken the scan —
// this is a one-shot, best-effort check, not a hot path, so we always favor
// finding a real symbol over speed:
//   - tryHarder   — spend the extra cycles to locate a symbol.
//   - tryRotate   — a cover barcode is often printed sideways (spine-aligned).
//   - tryInvert   — read light-on-dark barcodes too.
//   - tryDownscale— also search at reduced resolution (helps a large, crisp
//                   symbol on our high-res raster).
// maxNumberOfSymbols: 1 — we only need presence, so zxing can stop at the first
// hit. We do NOT restrict `formats`: leaving it empty searches every supported
// symbology (EAN/UPC, Code128, QR, …), maximizing the chance we spot whatever
// barcode is actually on the piece.
const READER_OPTIONS: ReaderOptions = {
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  tryDownscale: true,
  maxNumberOfSymbols: 1,
};

// Scan a single decoded image surface (Blob or ImageData). 'found' if zxing
// returns ≥1 valid symbol, else 'missing'.
async function scanSurface(input: Blob | ImageData): Promise<boolean> {
  const reader = await loadReader();
  const results = await reader.readBarcodes(input, READER_OPTIONS);
  return results.some((r) => r.isValid !== false);
}

// A fresh copy of an ImageData with the RGB channels inverted (alpha preserved).
// See the header note: zxing's own tryInvert runs AFTER binarization and won't
// read a light-on-dark barcode over a textured ground; inverting the pixels
// FIRST turns it into the clean dark-on-light image the binarizer expects.
function invertedImageData(img: ImageData): ImageData {
  const src = img.data;
  const out = new Uint8ClampedArray(src.length);
  for (let i = 0; i < src.length; i += 4) {
    out[i] = 255 - (src[i] ?? 0);
    out[i + 1] = 255 - (src[i + 1] ?? 0);
    out[i + 2] = 255 - (src[i + 2] ?? 0);
    out[i + 3] = src[i + 3] ?? 255;
  }
  return new ImageData(out, img.width, img.height);
}

// Scan a rasterized surface in BOTH polarities: as-is first (a normal
// dark-on-light barcode), then — only on a miss — an inverted copy (a
// light-on-dark barcode zxing's own tryInvert won't read). Returns at the first
// hit so a normal barcode never pays for the second pass.
async function scanBothPolarities(img: ImageData): Promise<boolean> {
  if (await scanSurface(img)) return true;
  return scanSurface(invertedImageData(img));
}

// Rasterize a PNG/JPEG blob to a (size-capped) ImageData so we can retry the
// inverted pass on it. Caps the longest side at PDF_SCAN_MAX_PX (never upscales —
// an image carries its own resolution) to bound memory, and flattens onto white
// so a transparent PNG reads as ink-on-paper. Returns null if the browser can't
// decode it — the caller then just keeps the direct-zxing result.
async function rasterizeImage(blob: Blob): Promise<ImageData | null> {
  try {
    const bitmap = await createImageBitmap(blob);
    try {
      const scale = Math.min(PDF_SCAN_MAX_PX / Math.max(bitmap.width, bitmap.height), 1);
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(bitmap, 0, 0, w, h);
      return ctx.getImageData(0, 0, w, h);
    } finally {
      bitmap.close();
    }
  } catch {
    return null;
  }
}

async function scanPdf(blob: Blob): Promise<BarcodeScan> {
  const pdfjs = await loadPdfjs();
  const data = new Uint8Array(await blob.arrayBuffer());
  const task = pdfjs.getDocument({ data });
  const doc = await task.promise;
  try {
    const pages = Math.min(doc.numPages, MAX_PDF_PAGES_TO_SCAN);
    // Tracks whether we actually rasterized + scanned EVERY page we intended to.
    // If a page can't get a 2D context (e.g. its high-res canvas exceeds the
    // browser's surface limit — more likely now that PDF_SCAN_MAX_PX is large),
    // we couldn't look there, so we must not later conclude 'missing'. A barcode
    // found on any page still short-circuits to 'found'.
    let scannedEveryPage = true;
    for (let n = 1; n <= pages; n++) {
      const page = await doc.getPage(n);
      const unscaled = page.getViewport({ scale: 1 });
      const scale = Math.min(
        PDF_SCAN_MAX_PX / Math.max(unscaled.width, unscaled.height),
        4,
      );
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        scannedEveryPage = false;
        continue;
      }
      // White backing so a transparent page renders as a sheet of paper —
      // barcodes are dark-on-light, so the binarizer needs a light ground.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport }).promise;
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      // Both polarities: a white-on-dark cover barcode only reads inverted.
      if (await scanBothPolarities(imageData)) return 'found';
    }
    // Only report 'missing' if we genuinely scanned every intended page and
    // found nothing; if a page couldn't be rasterized, we didn't truly look, so
    // 'unknown' (no advisory) is the honest result rather than a false "missing".
    return scannedEveryPage ? 'missing' : 'unknown';
  } finally {
    await doc.destroy();
  }
}

// Inspect a file's bytes for the presence of a barcode. Never throws; returns
// 'unknown' for anything we can't scan confidently. Accepts the raw bytes plus
// the declared content type + filename (the latter is the reliable type signal
// when an OS file picker reports an empty/odd MIME type — same fallback the
// thumbnail + page-count siblings use).
async function detectBarcodeInBlob(
  blob: Blob,
  content_type: string,
  filename: string,
): Promise<BarcodeScan> {
  // Oversized source → don't scan (see MAX_SCAN_SOURCE_BYTES).
  if (blob.size > MAX_SCAN_SOURCE_BYTES) return 'unknown';
  try {
    if (isPdf(content_type, filename)) return await scanPdf(blob);
    if (isDirectlyScannableImage(content_type, filename)) {
      // Fast path: zxing decodes the bytes itself (reads a normal,
      // dark-on-light barcode without a canvas round-trip).
      if (await scanSurface(blob)) return 'found';
      // Miss → rasterize ourselves and retry inverted, so a light-on-dark
      // barcode zxing's tryInvert won't read still registers. If the browser
      // can't decode the image, keep the direct (missing) result.
      const raster = await rasterizeImage(blob);
      if (raster && (await scanSurface(invertedImageData(raster)))) return 'found';
      return 'missing';
    }
    // SVG / TIFF / ZIP / AI / EPS / unknown — not scannable here.
    return 'unknown';
  } catch (err) {
    // Warn (not silent) so a wasm load / pdf.js parse failure is diagnosable
    // rather than looking like "the barcode check just didn't happen".
    console.warn(
      `[barcode] couldn't scan "${filename}" — leaving the barcode result unknown.`,
      err,
    );
    return 'unknown';
  }
}

// Convenience wrapper over a local File (the upload path scans the File the
// customer just picked, before/alongside the PUT to the provider).
export async function detectBarcodeInFile(file: File): Promise<BarcodeScan> {
  return detectBarcodeInBlob(file, file.type, file.name);
}
